import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, Upload, Check, Eraser, ChevronDown, ChevronUp, MapPin, Package, Truck } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

// ETA is a delivery DATE + a 2-hour WINDOW rather than an exact time — a
// dropdown is far easier to tap than a combined date+time picker on mobile.
// Keep in sync with the copies in JobStatus.jsx, DriverRuns.jsx and ClientJobs.jsx.
const ETA_WINDOWS = [
  { value: "06:00-08:00", label: "6:00 AM – 8:00 AM" },
  { value: "08:00-10:00", label: "8:00 AM – 10:00 AM" },
  { value: "10:00-12:00", label: "10:00 AM – 12:00 PM" },
  { value: "12:00-14:00", label: "12:00 PM – 2:00 PM" },
  { value: "14:00-16:00", label: "2:00 PM – 4:00 PM" },
  { value: "16:00-18:00", label: "4:00 PM – 6:00 PM" },
  { value: "18:00-20:00", label: "6:00 PM – 8:00 PM" },
];

const STATUS = {
  assigned: { c: "#19e6c8", l: "Assigned" },
  pending:  { c: "#19e6c8", l: "Assigned" },
  onboard:  { c: "#facc15", l: "Onboard" },
  done:     { c: "#22c55e", l: "Done" },
  futile:   { c: "#ef4444", l: "Futile" },
};

const fmt = (iso) => (iso ? formatDateTime(iso) : "");

async function compressImage(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, 1200 / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })),
        "image/jpeg", 0.75
      );
    };
    img.src = url;
  });
}

// The canvas mounts only once the collapsible section is opened (see
// ActionUpload), so setup can't run in a plain useEffect keyed on a ref
// object — a ref's identity never changes, so that effect would fire once
// at first render, before the canvas exists, and never re-run. A callback
// ref re-invokes on every mount/unmount of the actual DOM node instead.
function SignaturePad() {
  const drawing = useRef(false);
  const [node, setNode] = useState(null);
  const canvasRef = useCallback((el) => setNode(el), []);
  useEffect(() => {
    const cv = node;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = "#111"; ctx.lineWidth = 2.2; ctx.lineCap = "round";
    const pos = (e) => {
      const r = cv.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
      return { x: x * (cv.width / r.width), y: y * (cv.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawing.current = false; };
    cv.addEventListener("mousedown", start); cv.addEventListener("mousemove", move);
    cv.addEventListener("mouseup", end); cv.addEventListener("mouseleave", end);
    cv.addEventListener("touchstart", start, { passive: false });
    cv.addEventListener("touchmove", move, { passive: false });
    cv.addEventListener("touchend", end);
    return () => {
      cv.removeEventListener("mousedown", start); cv.removeEventListener("mousemove", move);
      cv.removeEventListener("mouseup", end); cv.removeEventListener("mouseleave", end);
      cv.removeEventListener("touchstart", start); cv.removeEventListener("touchmove", move);
      cv.removeEventListener("touchend", end);
    };
  }, [node]);
  const clear = () => {
    if (!node) return;
    const ctx = node.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, node.width, node.height);
  };
  return { canvasRef, clear, node };
}

// Mark "Arrived at …" — a one-tap action that records a timestamp, then shows a
// green confirmation once set (mirrors the driver portal arrival step).
function ArrivalRow({ label, at, onMark, accent }) {
  if (at) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold"
        style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>
        <Check size={16} /> {label} · {fmt(at)}
      </div>
    );
  }
  return (
    <button onClick={onMark}
      className="w-full px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 justify-center"
      style={{ background: accent + "22", color: accent }}>
      <MapPin size={16} /> {label}
    </button>
  );
}

// Lets the driver push a delivery date + window straight to the customer
// (e.g. running late/early) — mirrors the admin/in-house driver control.
function PushEtaCard({ token, job, onDone }) {
  const [etaDate, setEtaDate] = useState(job.eta_date || job.job_date || "");
  const [etaWindow, setEtaWindow] = useState(job.eta_window || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!etaDate || !etaWindow) { alert("Pick a date and a delivery window"); return; }
    setBusy(true);
    try {
      await pub.patch(`/driver-link/${token}/push-eta`, { eta_date: etaDate, eta_window: etaWindow });
      onDone && onDone();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const currentLabel = job.eta_date && job.eta_window
    ? `${formatDate(job.eta_date)} · ${ETA_WINDOWS.find((w) => w.value === job.eta_window)?.label || job.eta_window}`
    : null;

  return (
    <div className="px-3 py-2.5 rounded-xl border border-white/10 bg-white/[0.02]">
      <p className="text-xs font-semibold uppercase tracking-wider mb-2 text-white/50">Push ETA to Customer</p>
      <div className="flex gap-2 flex-wrap">
        <DatePicker value={etaDate} onChange={setEtaDate} className="flex-1 min-w-[9rem]" />
        <select
          value={etaWindow}
          onChange={(e) => setEtaWindow(e.target.value)}
          className="flex-1 min-w-[9rem] bg-white/5 border border-white/20 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#19e6c8]/50"
        >
          <option value="" className="bg-[#15151f]">Select window</option>
          {ETA_WINDOWS.map((w) => (
            <option key={w.value} value={w.value} className="bg-[#15151f]">{w.label}</option>
          ))}
        </select>
        <button type="button" onClick={submit} disabled={busy}
          className="px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap"
          style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>
          {busy ? "..." : "Push ETA"}
        </button>
      </div>
      {currentLabel && <p className="text-xs text-white/40 mt-2">Current: {currentLabel}</p>}
    </div>
  );
}

// Collapsible "Complete …" section: photos + name + (optional notes) + signature.
function ActionUpload({ token, label, sublabel, doneLabel, endpoint, nameField, nameLabel, withNotes, photoMax = 8, accent = "#19e6c8", completed, onDone }) {
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const { canvasRef, clear: clearSig, node: sigNode } = SignaturePad();

  if (completed) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-green-500/10 text-green-400 text-sm font-semibold">
        <Check size={16} /> {doneLabel}
      </div>
    );
  }

  const onPick = (e) => {
    const sel = Array.from(e.target.files || []).slice(0, photoMax - files.length);
    setFiles((f) => [...f, ...sel].slice(0, photoMax));
  };

  const submit = async () => {
    if (files.length === 0) { alert("Please add at least 1 photo."); return; }
    if (!sigNode) { alert("Please sign before submitting."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", await compressImage(f));
      fd.append("signature_data_url", sigNode.toDataURL("image/png"));
      if (name.trim()) fd.append(nameField, name.trim());
      if (withNotes && notes.trim()) fd.append("driver_notes", notes.trim());
      await pub.post(`/driver-link/${token}/${endpoint}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone && onDone();
    } catch (e) {
      alert("Submit failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-white font-semibold text-sm hover:bg-white/5"
      >
        <span className="text-left">
          <span className="block">{label}</span>
          {sublabel && <span className="block text-white/45 font-normal text-xs mt-0.5">{sublabel}</span>}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/10">
          {/* Photos */}
          <div>
            <p className="text-white/70 text-sm font-semibold mb-2">Photos ({files.length}/{photoMax})</p>
            <div className="grid grid-cols-4 gap-2">
              {files.map((f, i) => (
                <div key={i} className="relative aspect-square">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-lg border border-white/10" />
                  <button onClick={() => setFiles((a) => a.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">×</button>
                </div>
              ))}
              {files.length < photoMax && (
                <>
                  <label className="aspect-square rounded-lg border border-dashed border-white/30 flex flex-col items-center justify-center text-white/60 cursor-pointer hover:border-[#19e6c8]">
                    <Camera size={18} /><span className="text-[10px] mt-1">Camera</span>
                    <input type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
                  </label>
                  <label className="aspect-square rounded-lg border border-dashed border-white/30 flex flex-col items-center justify-center text-white/60 cursor-pointer hover:border-[#19e6c8]">
                    <Upload size={18} /><span className="text-[10px] mt-1">Upload</span>
                    <input type="file" accept="image/*" multiple hidden onChange={onPick} />
                  </label>
                </>
              )}
            </div>
          </div>
          {/* Name */}
          <div>
            <label className="block text-white/70 text-sm font-semibold mb-1">{nameLabel}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-[#19e6c8] text-sm" />
          </div>
          {/* Completion notes (delivery only) */}
          {withNotes && (
            <div>
              <label className="block text-white/70 text-sm font-semibold mb-1">Completion Notes <span className="text-white/30 font-normal">(optional)</span></label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Any notes about this delivery…" className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-[#19e6c8] text-sm resize-none" />
            </div>
          )}
          {/* Signature */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-white/70 text-sm font-semibold">Signature</p>
              <button onClick={clearSig} className="text-xs text-white/50 hover:text-white flex items-center gap-1"><Eraser size={12} /> Clear</button>
            </div>
            <canvas ref={canvasRef} width={600} height={200} className="w-full rounded-lg bg-white border border-white/20 cursor-crosshair touch-none" style={{ maxHeight: "200px" }} />
          </div>
          <button onClick={submit} disabled={busy} className="w-full py-3 rounded-xl text-[#0a0a0f] font-semibold flex items-center justify-center gap-2" style={{ background: accent }}>
            <Check size={16} /> {busy ? "Uploading…" : "Submit"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ExternalDriverView() {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    pub.get(`/driver-link/${token}`)
      .then(({ data }) => setJob(data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has expired."));
  }, [token, refresh]);

  const reload = () => setRefresh((r) => r + 1);

  const markArrival = async (which) => {
    try {
      await pub.patch(`/driver-link/${token}/${which}`);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center satin-bg text-white">
        <div className="text-center p-8">
          <p className="text-2xl font-bold mb-2 text-red-400">Link Closed</p>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center satin-bg text-white">
        <p className="text-white/50">Loading…</p>
      </div>
    );
  }

  const m = STATUS[job.status] || STATUS.assigned;
  const pickupDone = !!job.pickup_completed_at || job.has_sender_signature;
  const deliveryDone = !!job.has_pod || !!job.pod_completed_at;
  const handling = [
    ["Tail Lift", job.tail_lift],
    ["Hand Unload", job.hand_unload],
    ["Dangerous Goods", job.dangerous_goods],
    ["Inbound Connect", job.inbound_connect],
  ].filter(([, v]) => v === "Yes");

  return (
    <div className="min-h-screen satin-bg text-white" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <div className="max-w-lg mx-auto px-4 py-8" style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}>
        {/* Header */}
        <div className="mb-6 text-center">
          <p className="text-[#19e6c8] font-semibold tracking-widest text-xs uppercase mb-1">DRIVN FREIGHT · External Driver Portal</p>
          <h1 className="text-2xl font-bold">{job.job_ref}</h1>
          <p className="text-white/50 text-sm mt-1">{formatDate(job.job_date)}</p>
          <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: m.c + "22", color: m.c }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.c }} /> {m.l}
          </span>
        </div>

        {/* Job Info */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 mb-6 space-y-3">
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider">Pick-Up</p>
            <a href={`https://maps.google.com/?q=${encodeURIComponent(job.pickup_address)}`} target="_blank" rel="noopener noreferrer"
              className="text-white mt-0.5 inline-block underline decoration-dotted hover:text-[#19e6c8]">{job.pickup_address}</a>
            {(job.pickup_contact_name || job.pickup_contact_phone) && (
              <p className="text-white/60 text-sm mt-0.5">
                {[job.pickup_contact_name, job.pickup_contact_phone].filter(Boolean).join(" · ")}
              </p>
            )}
            {job.pickup_operating_hours && (
              <p className="text-sm mt-0.5" style={{ color: "#facc15" }}>Hours: {job.pickup_operating_hours}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider">Drop-Off</p>
            <a href={`https://maps.google.com/?q=${encodeURIComponent(job.dropoff_address)}`} target="_blank" rel="noopener noreferrer"
              className="text-white mt-0.5 inline-block underline decoration-dotted hover:text-[#22c55e]">{job.dropoff_address}</a>
            {(job.dropoff_contact_name || job.dropoff_contact_phone) && (
              <p className="text-white/60 text-sm mt-0.5">
                {[job.dropoff_contact_name, job.dropoff_contact_phone].filter(Boolean).join(" · ")}
              </p>
            )}
            {job.dropoff_operating_hours && (
              <p className="text-sm mt-0.5" style={{ color: "#facc15" }}>Hours: {job.dropoff_operating_hours}</p>
            )}
          </div>
          {(job.item_qty || job.item_type || job.volume_cbm || job.weight_kgs) && (
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider">Freight</p>
              <p className="text-white mt-0.5">
                {[
                  (job.item_qty || job.item_type) && `${job.item_qty || 0} × ${job.item_type || "items"}`,
                  job.volume_cbm ? `${job.volume_cbm} cbm` : null,
                  job.weight_kgs ? `${job.weight_kgs} kgs` : null,
                ].filter(Boolean).join(" · ")}
                {job.freight_type ? <span className="text-white/50"> ({job.freight_type})</span> : null}
              </p>
              {job.dimensions && <p className="text-white/60 text-sm mt-0.5">Dimensions: {job.dimensions}</p>}
            </div>
          )}
          {handling.length > 0 && (
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1.5">Handling</p>
              <div className="flex flex-wrap gap-2">
                {handling.map(([label]) => (
                  <span key={label} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "rgba(245,158,11,0.12)", color: "#f59e0b" }}>{label}</span>
                ))}
              </div>
            </div>
          )}
          {job.admin_remarks && (
            <div className="px-3 py-2.5 rounded-xl" style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.2)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "#f59e0b" }}>Notes from Dispatcher</p>
              <p className="text-white/80 text-sm">{job.admin_remarks}</p>
            </div>
          )}
          {job.notes && (
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider">Notes</p>
              <p className="text-white/70 text-sm mt-0.5 italic">"{job.notes}"</p>
            </div>
          )}
        </div>

        {/* Push ETA */}
        {!["done", "futile"].includes(job.status) && (
          <div className="mb-6">
            <PushEtaCard token={token} job={job} onDone={reload} />
          </div>
        )}

        {/* Pickup */}
        <h2 className="text-white/80 font-semibold mb-2 text-sm flex items-center gap-2"><Package size={15} className="text-[#facc15]" /> Pickup</h2>
        <div className="space-y-3 mb-6">
          <ArrivalRow label="Arrived at Pickup" at={job.pickup_arrived_at} onMark={() => markArrival("pickup-arrived")} accent="#19e6c8" />
          <ActionUpload token={token} label="Complete Pickup" sublabel={[job.pickup_contact_name, job.pickup_contact_phone].filter(Boolean).join(" · ")}
            doneLabel="Pickup completed" endpoint="pickup-signature"
            nameField="sender_name" nameLabel="Sender Name" photoMax={4} accent="#facc15" completed={pickupDone} onDone={reload} />
        </div>

        {/* Additional pickups/deliveries/stops */}
        {job.legs?.length > 0 && (
          <>
            <h2 className="text-white/80 font-semibold mb-2 text-sm">Additional Pickups / Deliveries / Stops</h2>
            <div className="space-y-3 mb-6">
              {job.legs.map((leg, i) => {
                // A pickup-type leg is signed by a sender, a dropoff-type leg by a
                // receiver — label the capture action accordingly instead of the
                // generic "Stop N".
                const legMeta = {
                  pickup:  { label: `Pickup ${i + 1}: ${leg.address}`,  doneLabel: `Pickup ${i + 1} completed`,  nameField: "sender_name",   nameLabel: "Sender Name",   accent: "#facc15" },
                  dropoff: { label: `Delivery ${i + 1}: ${leg.address}`, doneLabel: `Delivery ${i + 1} completed`, nameField: "receiver_name", nameLabel: "Receiver Name", accent: "#22c55e" },
                }[leg.stop_type] || {
                  label: `Stop ${i + 1}: ${leg.address}`, doneLabel: `Stop ${i + 1} completed`,
                  nameField: "signer_name", nameLabel: "Your Name", accent: "#19e6c8",
                };
                const sublabel = [leg.contact_name, leg.contact_phone].filter(Boolean).join(" · ");
                return (
                  <ActionUpload key={i} token={token} label={legMeta.label} sublabel={sublabel} doneLabel={legMeta.doneLabel}
                    endpoint={`legs/${i}/complete`} nameField={legMeta.nameField} nameLabel={legMeta.nameLabel} photoMax={8} accent={legMeta.accent}
                    completed={!!leg.completed_at} onDone={reload} />
                );
              })}
            </div>
          </>
        )}

        {/* Delivery */}
        <h2 className="text-white/80 font-semibold mb-2 text-sm flex items-center gap-2"><Truck size={15} className="text-[#22c55e]" /> Delivery</h2>
        <div className="space-y-3">
          <ArrivalRow label="Arrived at Delivery" at={job.delivery_arrived_at} onMark={() => markArrival("delivery-arrived")} accent="#22c55e" />
          <ActionUpload token={token} label="Complete Delivery" sublabel={[job.dropoff_contact_name, job.dropoff_contact_phone].filter(Boolean).join(" · ")}
            doneLabel="Delivery completed" endpoint="pod"
            nameField="receiver_name" nameLabel="Receiver Name" withNotes photoMax={8} accent="#22c55e" completed={deliveryDone} onDone={reload} />
        </div>
      </div>
    </div>
  );
}
